import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react-lite'

import clsx from 'clsx'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { Box, Collapse, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from '@material-ui/core'

import { BoxLoader } from './BoxLoader'
import { AccountSyncSwitch } from './AccountSyncSwitch'
import { AccountNavigationLink } from './AccountNavigationLink'
import { makeAccountNavStyles } from './AccountNavigation.styles'

import { appRoutes } from '../common/appRoutes'

import logsIcon from '../assets/logs.svg'
import printIcon from '../assets/print.svg'
import ordersIcon from '../assets/order.svg'
import importIcon from '../assets/import.svg'
import settingsIcon from '../assets/setting.svg'
import productsIcon from '../assets/products.svg'
import removeAccountIcon from '../assets/cross.svg'
import instructions from '../assets/instructions.svg'

import selectedLogsIcon from '../assets/logi.svg'
import selectedOrderIcon from '../assets/selectedOrder.svg'
import selectedPrintIcon from '../assets/selectedPrint.svg'
import selectedImportIcon from '../assets/selectedImport.svg'
import selectedSettingsIcon from '../assets/selectedSetting.svg'
import selectedProductsIcon from '../assets/selectedProducts.svg'

import { useBoolState } from '../hooks/commonHooks'
import { useMessages } from '../hooks/snackbarHooks'
import { useLogic, useStore } from '../hooks/storeHook'

import { integrationKeyByType } from '../types/integrationTypeUtils'
import { StoreAccountModel, StoreAccountProps } from '../types/accountProps'

export type AccountNavigationProps =
  StoreAccountProps & {
  isSelected: boolean
  title: string
  hasMoySkladApp: boolean
  onAccountChange: (account: StoreAccountModel) => void
}

const tabData = [{
  route: appRoutes.Store.products,
  title: 'Товары',
  imgSource: productsIcon,
  selectedImgSource: selectedProductsIcon
}, {
  route: appRoutes.Store.orders,
  title: 'Заказы',
  imgSource: ordersIcon,
  selectedImgSource: selectedOrderIcon
}, {
  route: appRoutes.Store.settings,
  title: 'Настройка',
  imgSource: settingsIcon,
  selectedImgSource: selectedSettingsIcon
}, {
  route: appRoutes.Store.print,
  title: 'Печать',
  imgSource: printIcon,
  selectedImgSource: selectedPrintIcon
}, {
  route: appRoutes.Store.import,
  title: 'Импорт',
  imgSource: importIcon,
  selectedImgSource: selectedImportIcon
}, {
  route: appRoutes.Store.syncLogs,
  title: 'Логи',
  imgSource: logsIcon,
  selectedImgSource: selectedLogsIcon
}] as const

export const AccountNavigation = observer(
  ({
     isSelected,
     title,
     account,
     onAccountChange
   }: AccountNavigationProps) => {
    const classes = useStyles()
    const {transitions: {duration}} = useTheme()

    const {syncStore} = useStore()
    const logic = useLogic()
    const history = useHistory()

    const {showSuccess, showError} = useMessages()

    const [isDeleting, setDeleting, setDeleted] = useBoolState()

    const listRef = useRef<HTMLUListElement>(null)
    const [listHeight, setListHeight] = useState(210 /* default */)

    useEffect(() => {
      if (isSelected && listRef.current) {
        const newHeight = listRef.current.getBoundingClientRect().height
        setListHeight(prevHeight => Math.max(newHeight, prevHeight))
      }
    }, [isSelected])

    const onDeleteAccount =
      async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        const {integrationType} = account
        const integration = integrationKeyByType[integrationType]

        try {
          if (!await logic.showDialog(
            'Удалённый аккаунт невозможно восстановить!', {title: 'Удалить аккаунт?', acceptButton: 'Удалить'})) {
            return
          }

          setDeleting()

          await logic.deleteAccount(account)
          showSuccess('Аккаунт удалён!')
        } catch {
          showError('Ошибка при удалении аккаунта!')
          return
        } finally {
          setDeleted()
        }

        const firstAccountId = syncStore.getStoreAccounts(integrationType)[0]?.id

        if (firstAccountId) {
          history.push(appRoutes.Store.products.makeUrl({integration, accountId: firstAccountId}))
        } else {
          history.push(appRoutes.MoySklad.account)
        }
      }

    if (isDeleting) {
      return (
        <Box className={clsx('nav_account', isSelected && 'nav_account_selected')}>
          <BoxLoader text="Аккаунт удаляется. Пожалуйста, подождите."/>
        </Box>
      )
    }

    return (
      <Box className={clsx('nav_account', isSelected && 'nav_account_selected')}>
        <Box
          className={classes.accountInfo}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => !isSelected && onAccountChange(account)}
          aria-disabled={isSelected}
        >
          {/* TODO: (routing) Ask if multiline + tooltip are needed 1/2 */}
          <Tooltip arrow placement="bottom" title={title.length > 40 ? title : ''}>
            <Typography className={classes.accountName}>
              {title}
            </Typography>
          </Tooltip>

          <Box display="flex" justifyContent="center" alignItems="center">
            {
              isSelected &&
              <Tooltip title="Удалить аккаунт..." arrow placement="bottom">
                <img
                  className={classes.deleteIcon}
                  src={removeAccountIcon}
                  alt="Удалить"
                  onClick={onDeleteAccount}/>
              </Tooltip>
            }

            <AccountSyncSwitch
              account={account}
              readOnly={!isSelected}
              setSync={logic.getSetAccountSyncFunc(account.integrationType)}
            />
          </Box>
        </Box>

        <Collapse in={isSelected} timeout={duration.shortest}>
          <Box className="nav_account_actions">
            {
              isSelected
              ?
              <List ref={listRef}>
                <ListItem
                  component="a"
                  href="https://help.mpsklad.ru/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={clsx('nav_account_link', classes.listItem)}
                  button
                  disableRipple
                >
                  <ListItemIcon className={clsx(classes.listItemIcon, 'nav_account_link')}>
                    <img src={instructions} alt="Инструкция"/>
                  </ListItemIcon>

                  <ListItemText className={clsx(classes.listItemText, 'nav_account_link')} primary="Инструкция"/>
                </ListItem>

                {
                  tabData.map(tab => <AccountNavigationLink key={tab.route.template} account={account} {...tab}/>)
                }
              </List>
              :
              <Box width="100%" height={listHeight}>
                {/* A stub to allow height transition during when hidden */}
              </Box>
            }
          </Box>
        </Collapse>
      </Box>
    )
  })

const useStyles = makeStyles(makeAccountNavStyles)